import { Form } from 'antd';
import { InventoryDocumentType, ProductVersion } from 'models';
import { useCallback, useEffect, useState } from 'react';
import useAuthContext from 'stores/auth-context';
import {
  useCreateInventoryCheckOrderMutation,
  useGenerateCodeMutation,
  useGetInventoryCheckOrderByIdQuery,
  useGetInventoryCheckOrderDupByIdQuery,
  useGetProductsQuery,
  useGetStatusQuery,
  useUpdateInventoryCheckOrderMutation,
} from 'services';
import { ProductData, QueryParams } from 'types';
import { useDebouncedCallback } from 'use-debounce';
import { generateRandomBarcode } from 'utils';
import { showAlertNotification } from 'components/common';
import { useNavigate } from 'react-router-dom';

export const useCreateCheckOrder = () => {
  const [form] = Form.useForm();
  const params = new URLSearchParams(window.location.search);
  const orderId = params.get('id') || '';
  const copyId = params.get('copyId') || '';
  const isCopyMode = Boolean(copyId && !orderId);
  const [selectedProducts, setSelectedProducts] = useState<ProductVersion[]>([]);
  const { profile, loading } = useAuthContext();
  const [selectedProduct, setSelectedProduct] = useState<ProductData | undefined>(undefined);
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [generateCode, { isLoading: isLoadingGenerateCode }] = useGenerateCodeMutation();
  const [code, setCode] = useState<string | undefined>(undefined);
  const [createCheckOrder, { isLoading: isLoadingCreateCheckOrder }] = useCreateInventoryCheckOrderMutation();
  const [updateCheckOrder, { isLoading: isLoadingUpdateCheckOrder }] = useUpdateInventoryCheckOrderMutation();
  const navigate = useNavigate();
  const { data: editCheckOrderData, isLoading: isLoadingEditCheckOrderData } = useGetInventoryCheckOrderByIdQuery(
    {
      id: orderId,
      query: {
        include: 'status|createdBy.baseUser|inventoryDocumentItems',
      },
    },
    { skip: !orderId },
  );
  const { data: dupCheckOrderData, isLoading: isLoadingDupCheckOrderData } = useGetInventoryCheckOrderDupByIdQuery(
    copyId,
    { skip: !isCopyMode || !copyId },
  );
  const checkOrderData = isCopyMode ? dupCheckOrderData : editCheckOrderData;
  const isLoadingCheckOrderData = isCopyMode ? isLoadingDupCheckOrderData : isLoadingEditCheckOrderData;
  const [filtered, setFiltered] = useState<QueryParams>({
    size: 20,
    page: 1,
    include: 'product.productVersions|category|status|brand|baseUnit|currencyUnit|productImages.image',
    filter: 'isActive:eq:true&&status.code:eq:pr-active',
    sort: 'updatedAt:desc',
  });
  const { data: statusData } = useGetStatusQuery({
    query: {
      filter: 'isActive:eq:true&&type:eq:' + InventoryDocumentType.INVENTORY_CHECK_ORDER,
    },
  });
  const { data: productVersionsData, isLoading: isLoadingProducts } = useGetProductsQuery({
    query: filtered,
  });
  const inprogressId = statusData?.data?.find((status) => status.code === 'invcheck-inprogress')?.id;
  const completedId = statusData?.data?.find((status) => status.code === 'invcheck-completed')?.id;
  const debouncedSearch = useDebouncedCallback((searchTerm: string) => {
    setFiltered(
      (prev) =>
        ({
          ...prev,
          size: searchTerm ? 'unlimited' : 20,
          page: 1,
          searchKey: 'name|color|dimension|sizeSpec|product.code',
          searchValue: searchTerm || '',
        }) as unknown as QueryParams,
    );
  }, 300); // Debounce 300ms

  useEffect(() => {
    const codeGen = async () => {
      if (!orderId) {
        const response = await generateCode({ code: 'PK' });
        setCode(response.data);
      }
    };
    codeGen();
  }, [orderId, generateCode]);

  useEffect(() => {
    if (orderId && checkOrderData?.data?.code) {
      setCode(checkOrderData.data.code);
    }
  }, [orderId, checkOrderData?.data?.code]);

  useEffect(() => {
    if (profile) {
      form.setFieldsValue({
        userName: profile?.data.baseUser?.name || '',
      });
    }

    form.setFieldsValue({
      code: isCopyMode ? code : checkOrderData?.data?.code || code,
    });

    if (checkOrderData) {
      setDescription(checkOrderData?.data?.description || '');
      setSelectedProducts(
        checkOrderData?.data?.inventoryDocumentItems?.map((item) => ({
          id: item.productVersionId,
          baseUnitId: item.unitId,
          name: item.name,
          quantity: item.quantity,
          actualQuantity: isCopyMode ? undefined : item.actualQuantity,
          costPrice: item.costPrice,
          total: item.total,
          categoryId: item.category,
          reason: isCopyMode ? undefined : item.description,
          color: item.color,
          sizeSpec: item.sizeSpec,
          dimension: item.productDimension,
        })) || [],
      );
    }
  }, [profile, code, checkOrderData, isCopyMode, form]);

  const handleSelectProductToList = (product: ProductVersion) => {
    if (selectedProducts.some((p) => p.id === product.id)) return;
    setSelectedProducts((prev) => [...prev, product]);
  };

  const handleSelectProducts = (products: ProductVersion[]) => {
    setSelectedProducts(products);
  };

  const handleRemoveProduct = (productId: string) => {
    setSelectedProducts((prev) => prev.filter((p) => p.id !== productId));
  };

  const handleProductSearch = (searchTerm: string) => {
    debouncedSearch(searchTerm);
  };

  const handleSelectAll = () => {
    const availableProducts = productVersionsData?.data?.rows || [];
    const newProducts = availableProducts.filter((product) => !selectedProducts.some((p) => p.id === product.id));
    setSelectedProducts((prev) => [...prev, ...newProducts]);

    showAlertNotification({
      type: 'success',
      title: 'Đã thêm sản phẩm',
      message: `Đã thêm ${newProducts.length} sản phẩm vào danh sách`,
    });
  };

  const handleSaveActualQuantity = (productId: string, actualQuantity: number | null, reason?: string) => {
    const updatedProducts = selectedProducts.map((product) => {
      if (product.id === productId) {
        return {
          ...product,
          actualQuantity,
          reason,
        };
      }
      return product;
    });
    setSelectedProducts(updatedProducts);
    setSelectedProduct(undefined); // Reset selected product
  };

  const handleSelectProduct = (product: ProductData) => {
    setSelectedProduct(product);
  };
  const createOrderPayload = useCallback(
    (isPublishing: boolean) => {
      return {
        code: orderId ? checkOrderData?.data?.code || '' : code || '',
        barcode: orderId ? checkOrderData?.data?.barcode || '' : generateRandomBarcode(),
        total: selectedProducts.reduce((acc, product) => {
          const expectedQuantity = product.product?.quantity ?? product.quantity ?? 0;
          const effectiveActualQuantity = product.actualQuantity ?? expectedQuantity;
          const itemCostPrice = Number(product.costPrice) || 0;
          return acc + effectiveActualQuantity * itemCostPrice;
        }, 0),
        currentStep: 0,
        description: description,
        statusId: isPublishing ? completedId || '' : inprogressId || '',
        type: InventoryDocumentType.INVENTORY_CHECK_ORDER,
        inventoryDocumentItems: selectedProducts.map((item) => {
          const expectedQuantity = item.product?.quantity ?? item.quantity ?? 0;
          const itemCostPrice = Number(item.costPrice) ?? 0;
          const effectiveActualQuantity = item.actualQuantity ?? expectedQuantity;

          return {
            productVersionId: item.id,
            unitId: item.baseUnitId,
            unitConvertValue: 1,
            name: item.name,
            color: item.color,
            sizeSpec: item.sizeSpec,
            productDimension: item.dimension,
            category: item.category?.name || item.categoryId,
            quantity: expectedQuantity,
            actualQuantity: effectiveActualQuantity,
            description: item.reason ?? '',
            costPrice: itemCostPrice,
            total: expectedQuantity * itemCostPrice,
          };
        }),
      };
    },
    [code, description, selectedProducts, inprogressId, completedId],
  );
  const handleSubmit = useCallback(
    async (isPublishing: boolean) => {
      if (!selectedProducts.length) {
        showAlertNotification({
          type: 'warning',
          title: 'Danh sách rỗng',
          message: 'Vui lòng thêm ít nhất một sản phẩm để kiểm kho',
        });
        return;
      }

      const payload = createOrderPayload(isPublishing);

      try {
        let result;
        if (isPublishing) {
          if (orderId) {
            result = await updateCheckOrder({
              ...payload,
              id: orderId,
            });
          } else {
            result = await createCheckOrder(payload);
          }
        } else {
          if (orderId) {
            result = await updateCheckOrder({
              ...payload,
              id: orderId,
            });
          } else {
            result = await createCheckOrder(payload);
          }
        }

        if (result.error) {
          throw new Error('Đã xảy ra lỗi khi gọi API');
        }

        showAlertNotification({
          type: 'success',
          title: isPublishing ? 'Kiểm kho thành công' : 'Lưu đợi kiểm kho thành công',
          message: isPublishing
            ? 'Hoàn thành kiểm kho thành công'
            : 'Phiếu kiểm kho đã được lưu lại và đợi kiểm kho sau',
        });
        if (isPublishing) {
          navigate('/management/inventory/check-orders/detail/' + (result.data?.data?.id || ''));
        } else {
          navigate('/management/inventory/check-orders');
        }
      } catch (error) {
        console.error('Error creating/updating check order:', error);

        showAlertNotification({
          type: 'error',
          title: 'Kiểm kho thất bại',
          message: 'Không thể hoàn thành kiểm kho',
        });
      }
    },
    [selectedProducts, description, createOrderPayload, createCheckOrder, updateCheckOrder],
  );
  return {
    checkOrderData,
    form,
    selectedProducts,
    setSelectedProducts,
    selectedProduct,
    setSelectedProduct,
    description,
    setDescription,
    productVersionsData,
    isLoadingProducts,
    handleSelectProductToList,
    handleSelectProducts,
    handleRemoveProduct,
    handleProductSearch,
    handleSelectAll,
    handleSaveActualQuantity,
    handleSelectProduct,
    handleSubmit,
    profile,
    isCopyMode,
    isLoading: isLoadingCreateCheckOrder || isLoadingUpdateCheckOrder,
    loading: loading || isLoadingGenerateCode || isLoadingCheckOrderData,
  };
};
